import { Dispatch, GetState } from "common/types";

import { getAllCourseContentIds } from "seneca-common/features/course/state";
import { ModuleRegistry } from "seneca-common/features/module/registry";
import {
  fetchContentStudyStats,
  fetchCourseStudyStats,
  fetchSectionStudyStats,
  fetchUserStudyStats,
  sendSessionReport
} from "seneca-common/features/stats/service/clientActions";

import { getAllStudiedCourseIdsFromSessions } from "../../selectors/session";
import { generateSessionReportsForAllFinishedSessions } from "../../selectors/sessionReport";
import { reconcileContentSectionAndCourseStats } from "./reconcileContentSectionAndCourseStats";
import { reconcileUserStats } from "./reconcileUserStats";

const moduleRegistry = ModuleRegistry.getInstance();

export function reconcileStudyStats() {
  return async function (dispatch: Dispatch, getState: GetState) {
    // STEP 1: generate session reports for any completed sessions in the store state
    let state = getState();

    const studiedCourseIds = getAllStudiedCourseIdsFromSessions(state);

    const sessionReports = generateSessionReportsForAllFinishedSessions(
      state,
      moduleRegistry
    );

    // User stats can be reconciled straight away
    const userStatsFetch = dispatch(fetchUserStudyStats());
    const userStatsReconcilation = reconcileUserStats(
      sessionReports,
      userStatsFetch,
      dispatch
    );

    const statsReconciliations = studiedCourseIds.map((courseId: string) => {
      const relevantSessionReports = sessionReports.filter(
        report => report.session!.courseId === courseId
      );
      // STEP 2: fetch all stats for all courses that have been studied whilst not signed in
      const contentStatsFetch = dispatch(fetchContentStudyStats(courseId));
      const sectionStatsFetch = dispatch(fetchSectionStudyStats(courseId));
      const courseStatsFetch = dispatch(fetchCourseStudyStats(courseId));

      // STEP 3: for all received stats - apply relevant module/session study events to them (reconciliation)

      //   - Content stats must be reconciled one session at a time, as they are
      //     used to update the starting and ending proficiencies of each session.
      //     The final section and course proficiencies are sent to the server along
      //     with the course and section stats - as a snapshot to be used on the dashboard
      return reconcileContentSectionAndCourseStats(
        relevantSessionReports,
        contentStatsFetch,
        getAllCourseContentIds(state, courseId),
        sectionStatsFetch,
        courseStatsFetch,
        dispatch,
        getState
      );
    });

    // STEP 4: once old stats have been fetched from server and reconciled with sessions,
    // regenerate and send session reports (with up-to-date proficiencies) to update the server stats
    await Promise.all([...statsReconciliations, userStatsReconcilation]);

    state = getState();
    await Promise.all(
      generateSessionReportsForAllFinishedSessions(state, moduleRegistry)
        .toList()
        .map(sendSessionReport)
    );
  };
}
